import React from 'react';

interface LoadingStateProps {
  type?: 'card' | 'text' | 'list';
  count?: number;
  lines?: number;
  className?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  type = 'card',
  count = 1,
  lines = 3,
  className = '',
}) => {
  if (type === 'card') {
    return (
      <div className={`space-y-4 ${className}`}>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6"
          >
            <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-6 w-1/3 mb-4 rounded-lg"></div>
            <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-4 w-full mb-2 rounded-lg"></div>
            <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-4 w-2/3 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'text') {
    return (
      <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className="animate-pulse bg-slate-200 dark:bg-slate-800 h-4 w-full rounded-lg"
          ></div>
        ))}
      </div>
    );
  }

  if (type === 'list') {
    return (
      <div className={`space-y-3 ${className}`}>
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-10 w-10 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-4 w-1/4 rounded-lg"></div>
              <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-3 w-1/2 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-primary-600 ${sizeClasses[size]}`}
      ></div>
    </div>
  );
};

interface LoadingOverlayProps {
  message?: string;
  className?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message, className = '' }) => {
  return (
    <div
      className={`fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 ${className}`}
    >
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-4">
        <LoadingSpinner size="lg" />
        {message && <p className="text-slate-700 dark:text-slate-200 font-medium">{message}</p>}
      </div>
    </div>
  );
};
